import { motion } from "framer-motion";
import { APPLY_URL, PRELIMS_CRASH_URL, PROGRAM_FEATURES, CONTACT, PROGRAM_EXAMPLES } from "../lib/constants";
import ExampleGallery from "./ExampleGallery";
import {
  Calendar,
  GraduationCap,
  Sparkles,
  Check,
  BookOpen,
  Award,
  Zap,
  Target,
  Clock,
  Crown,
  Feather,
  Users,
} from "lucide-react";

const ease = [0.76, 0, 0.24, 1];

// ============================================================
// THEMES — one palette per program
// ============================================================
const THEMES = {
  royal: {
    // Program 1 — Royal Blue & White (Professional, Academic, Trustworthy)
    bg: "#0F2A5E",
    bgSoft: "#0B2050",
    accent: "#D4AF37", // subtle regal gold micro-accent
    text: "#F5F5F7",
    textMuted: "rgba(245,245,247,0.75)",
    border: "rgba(245,245,247,0.18)",
    borderStrong: "rgba(245,245,247,0.35)",
    pill: "rgba(245,245,247,0.12)",
    cardBg: "rgba(255,255,255,0.06)",
    ctaBg: "#F5F5F7",
    ctaText: "#0F2A5E",
    ctaBgAlt: "transparent",
    strike: "rgba(245,245,247,0.5)",
    gradient:
      "radial-gradient(ellipse 60% 55% at 100% 0%, rgba(255,255,255,0.14), transparent 60%), radial-gradient(ellipse 50% 40% at 0% 100%, rgba(212,175,55,0.18), transparent 60%)",
    shadow: "0 30px 80px -25px rgba(15,42,94,0.55)",
  },
  emerald: {
    // Program 2 — Emerald Green & Gold (Growth, Mentorship, Success)
    bg: "#0B3B2E",
    bgSoft: "#082D24",
    accent: "#E7C46A", // warm gold
    accentSoft: "#F2D98A",
    text: "#F4EEDC",
    textMuted: "rgba(244,238,220,0.75)",
    border: "rgba(231,196,106,0.28)",
    borderStrong: "rgba(231,196,106,0.55)",
    pill: "rgba(231,196,106,0.14)",
    cardBg: "rgba(231,196,106,0.06)",
    ctaBg: "#E7C46A",
    ctaText: "#0B3B2E",
    strike: "rgba(244,238,220,0.5)",
    gradient:
      "radial-gradient(ellipse 55% 55% at 100% 100%, rgba(231,196,106,0.22), transparent 60%), radial-gradient(ellipse 45% 40% at 0% 0%, rgba(244,238,220,0.08), transparent 60%)",
    shadow: "0 30px 80px -25px rgba(11,59,46,0.55)",
  },
  burgundy: {
    // Program 3 — Deep Burgundy/Purple & Silver (Exclusive, Premium, Elite)
    bg: "#3B1330",
    bgSoft: "#2A0E23",
    accent: "#C7C4C2", // brushed silver
    accentSoft: "#E5E1DE",
    text: "#F3EDE8",
    textMuted: "rgba(243,237,232,0.75)",
    border: "rgba(199,196,194,0.28)",
    borderStrong: "rgba(199,196,194,0.55)",
    pill: "rgba(199,196,194,0.12)",
    cardBg: "rgba(199,196,194,0.06)",
    ctaBg: "#C7C4C2",
    ctaText: "#3B1330",
    strike: "rgba(243,237,232,0.5)",
    gradient:
      "radial-gradient(ellipse 55% 55% at 0% 0%, rgba(199,196,194,0.16), transparent 60%), radial-gradient(ellipse 55% 55% at 100% 100%, rgba(199,196,194,0.10), transparent 60%)",
    shadow: "0 30px 80px -25px rgba(59,19,48,0.55)",
  },
};

// ============================================================
// Preview Compare Cards data
// ============================================================
const previewCards = [
  {
    id: "foundation",
    theme: THEMES.royal,
    number: "01",
    tag: "Flagship",
    title: "Foundation",
    subtitle: "UPSC CSE 2027",
    duration: "Aug 2026 → 2027",
    price: "₹5,999",
    strike: "₹8,999",
    Icon: Crown,
  },
  {
    id: "prelims",
    theme: THEMES.emerald,
    number: "02",
    tag: "Prelims Blitz",
    title: "Crash Course",
    subtitle: "Revision Batch",
    duration: "Feb – Apr 2027",
    price: "₹5,499",
    strike: "₹7,999",
    Icon: Zap,
  },
  {
    id: "answer-writing",
    theme: THEMES.burgundy,
    number: "03",
    tag: "Exclusive",
    title: "365-Day Mains",
    subtitle: "Answer Writing Eval.",
    duration: "Year-round",
    price: "By Enrolment",
    strike: null,
    Icon: Feather,
  },
];

// ============================================================
// Info Card (themed sub-card)
// ============================================================
const InfoCard = ({ Icon, label, value, sub, theme }) => (
  <div
    className="p-6 md:p-7 rounded-2xl backdrop-blur-sm"
    style={{
      background: theme.cardBg,
      border: `1px solid ${theme.border}`,
    }}
  >
    <div className="flex items-center gap-3 mb-5">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center"
        style={{ background: theme.pill }}
      >
        <Icon size={16} strokeWidth={1.5} style={{ color: theme.accent }} />
      </div>
      <span
        className="text-[10px] tracking-[0.28em] uppercase font-semibold"
        style={{ color: theme.textMuted }}
      >
        {label}
      </span>
    </div>
    <div
      className="font-display text-2xl md:text-3xl leading-tight"
      style={{ fontVariationSettings: '"SOFT" 40', color: theme.text }}
    >
      {value}
    </div>
    <div className="mt-2 text-sm font-light" style={{ color: theme.textMuted }}>
      {sub}
    </div>
  </div>
);

// ============================================================
// Themed Card wrapper
// ============================================================
const ThemedCard = ({ theme, children, id, testid }) => (
  <motion.div
    id={id}
    data-testid={testid}
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 1.0, ease }}
    className="relative rounded-[28px] overflow-hidden"
    style={{
      background: theme.bg,
      color: theme.text,
      boxShadow: theme.shadow,
    }}
  >
    {/* Themed gradient overlay */}
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{ background: theme.gradient }}
    />
    <div className="relative p-8 md:p-12 lg:p-16">{children}</div>
  </motion.div>
);

// ============================================================
// Program Section Header (inside a themed card)
// ============================================================
const ProgramHeader = ({ number, tag, title, tagline, theme, Icon }) => (
  <div className="grid grid-cols-12 gap-6 md:gap-10 items-start mb-10 md:mb-14">
    <div className="col-span-12 lg:col-span-9">
      <div className="flex items-center gap-3 mb-6">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center"
          style={{ background: theme.pill, border: `1px solid ${theme.border}` }}
        >
          <Icon size={18} strokeWidth={1.4} style={{ color: theme.accent }} />
        </div>
        <span
          className="font-display italic text-2xl"
          style={{ color: theme.accent, fontVariationSettings: '"SOFT" 100' }}
        >
          {number}
        </span>
        <span className="h-px w-8" style={{ background: theme.borderStrong }} />
        <span
          className="text-[10px] tracking-[0.32em] uppercase font-semibold"
          style={{ color: theme.textMuted }}
        >
          {tag}
        </span>
      </div>
      <h3
        className="font-display font-light text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.02em]"
        style={{ fontVariationSettings: '"SOFT" 60', color: theme.text }}
      >
        {title}
      </h3>
    </div>
    <div className="col-span-12 lg:col-span-3 lg:pt-8">
      <p
        className="font-display italic text-lg md:text-xl leading-snug"
        style={{
          color: theme.textMuted,
          fontVariationSettings: '"SOFT" 100',
        }}
      >
        {tagline}
      </p>
    </div>
  </div>
);

// ============================================================
// MAIN
// ============================================================
export default function Program() {
  return (
    <section
      id="program"
      data-testid="program-section"
      className="relative py-24 md:py-32 lg:py-40 bg-cream border-t border-ink-15 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 55% 45% at 88% 8%, rgba(217,105,28,0.06), transparent 65%)",
        }}
      />

      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 relative">
        {/* ============ SECTION HEADER ============ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease }}
          className="inline-flex items-center gap-2 bg-ink text-cream px-4 md:px-5 py-2 md:py-2.5 rounded-full"
        >
          <Sparkles size={13} className="text-saffron" strokeWidth={1.8} />
          <span className="text-[10px] md:text-[11px] tracking-[0.28em] uppercase font-semibold">
            Three Ways to Prepare
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.1, ease }}
          className="mt-8 md:mt-10 font-display font-light text-5xl md:text-7xl lg:text-[7.5rem] leading-[0.98] tracking-[-0.03em] text-ink max-w-6xl"
          style={{ fontVariationSettings: '"SOFT" 60' }}
        >
          Three programs. <span className="italic text-saffron">One resolve.</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2, ease }}
          className="mt-8 text-lg md:text-xl text-ink/75 max-w-3xl font-light leading-relaxed"
        >
          A full year of mentorship, a three-month Prelims blitz, or year-round Mains evaluation — each with its own character, pick the one your preparation needs today.
        </motion.p>

        {/* ============ COMPARE PREVIEW CARDS ============ */}
        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {previewCards.map((p, i) => (
            <motion.a
              key={p.id}
              href={`#program-${p.id}`}
              data-testid={`preview-${p.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease }}
              whileHover={{ y: -6 }}
              className="group relative rounded-3xl overflow-hidden block"
              style={{
                background: p.theme.bg,
                color: p.theme.text,
                boxShadow: p.theme.shadow,
              }}
            >
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: p.theme.gradient }}
              />
              <div className="relative p-7 md:p-8 min-h-[340px] flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-8">
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center"
                      style={{ background: p.theme.pill, border: `1px solid ${p.theme.border}` }}
                    >
                      <p.Icon size={18} strokeWidth={1.4} style={{ color: p.theme.accent }} />
                    </div>
                    <span
                      className="font-display italic text-3xl"
                      style={{ color: p.theme.accent, fontVariationSettings: '"SOFT" 100' }}
                    >
                      {p.number}
                    </span>
                  </div>
                  <div
                    className="text-[10px] tracking-[0.32em] uppercase font-semibold mb-2"
                    style={{ color: p.theme.accent }}
                  >
                    {p.tag}
                  </div>
                  <div
                    className="font-display text-3xl md:text-4xl leading-tight mb-1"
                    style={{ fontVariationSettings: '"SOFT" 60', color: p.theme.text }}
                  >
                    {p.title}
                  </div>
                  <div
                    className="font-display italic text-lg"
                    style={{ color: p.theme.textMuted, fontVariationSettings: '"SOFT" 100' }}
                  >
                    {p.subtitle}
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t" style={{ borderColor: p.theme.border }}>
                  <div className="flex items-baseline justify-between">
                    <span
                      className="text-[10px] tracking-[0.28em] uppercase font-semibold"
                      style={{ color: p.theme.textMuted }}
                    >
                      {p.duration}
                    </span>
                    <div className="flex items-baseline gap-2">
                      {p.strike && (
                        <span
                          className="font-display italic text-sm line-through"
                          style={{ color: p.theme.strike }}
                        >
                          {p.strike}
                        </span>
                      )}
                      <span
                        className="font-display text-2xl md:text-3xl"
                        style={{ fontVariationSettings: '"SOFT" 40', color: p.theme.text }}
                      >
                        {p.price}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between text-xs">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ color: p.theme.accent }}>
                      Learn more
                    </span>
                    <span
                      className="text-lg transform group-hover:translate-x-1 transition-transform duration-300"
                      style={{ color: p.theme.accent }}
                    >
                      →
                    </span>
                  </div>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* ============ PROGRAM 01 · FOUNDATION (ROYAL BLUE) ============ */}
        <div className="mt-20 md:mt-24">
          <ThemedCard id="program-foundation" testid="program-foundation" theme={THEMES.royal}>
            <ProgramHeader
              number="01"
              tag="Flagship · Full-year Mentorship"
              title={
                <>
                  UPSC CSE 2027{" "}
                  <span className="italic font-light" style={{ color: THEMES.royal.accent }}>
                    Mentorship Program.
                  </span>
                </>
              }
              tagline="A full-year, live-mentored preparation conducted personally by Himanshu Sir."
              theme={THEMES.royal}
              Icon={Crown}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              <InfoCard Icon={Calendar} label="Batch Starts" value="8 August, 2026" sub="Weekly live sessions" theme={THEMES.royal} />
              <InfoCard Icon={GraduationCap} label="Mode" value="Online · Evening" sub="From anywhere in Bharat" theme={THEMES.royal} />
              <InfoCard Icon={Sparkles} label="Orientation" value="1st Week of August" sub="Free · notified by email" theme={THEMES.royal} />
            </div>

            {/* Fee + CTA */}
            <div className="mt-10 md:mt-12 flex flex-wrap items-center gap-4 md:gap-6">
              <a
                href={APPLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="program-apply-btn"
                className="px-8 md:px-10 py-4 md:py-5 text-sm rounded-full font-semibold inline-flex items-center gap-3 hover:scale-[1.02] transition-transform duration-300"
                style={{ background: THEMES.royal.ctaBg, color: THEMES.royal.ctaText }}
              >
                Register for the 2027 Batch — ₹5,999 <span aria-hidden>→</span>
              </a>
              <a
                href="#features"
                data-testid="program-features-btn"
                className="inline-flex items-center px-7 md:px-8 py-4 md:py-5 text-sm rounded-full font-medium transition-colors duration-300"
                style={{
                  border: `1px solid ${THEMES.royal.borderStrong}`,
                  color: THEMES.royal.text,
                }}
              >
                See what's covered
              </a>
              <div className="flex items-center gap-3">
                <span
                  className="font-display italic text-lg md:text-xl line-through"
                  style={{ color: THEMES.royal.strike }}
                >
                  ₹8,999
                </span>
                <span className="text-sm font-light" style={{ color: THEMES.royal.textMuted }}>
                  · Launch price, limited seats
                </span>
              </div>
            </div>

            {/* Features */}
            <div
              id="features"
              className="mt-16 md:mt-20 pt-10 md:pt-14 grid grid-cols-12 gap-6 md:gap-10 items-start border-t"
              style={{ borderColor: THEMES.royal.border }}
            >
              <div className="col-span-12 md:col-span-5">
                <h4
                  className="font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-[-0.02em]"
                  style={{ fontVariationSettings: '"SOFT" 60', color: THEMES.royal.text }}
                >
                  What's{" "}
                  <span className="italic" style={{ color: THEMES.royal.accent }}>
                    covered
                  </span>
                  , exactly.
                </h4>
                <p
                  className="mt-5 text-base font-light leading-relaxed max-w-md"
                  style={{ color: THEMES.royal.textMuted }}
                >
                  The launch price includes the entire mentorship machinery — nothing sold separately.
                </p>
              </div>
              <div className="col-span-12 md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
                {PROGRAM_FEATURES.map((f) => (
                  <div
                    key={f.n}
                    className="flex items-start gap-4 py-4 border-b"
                    style={{ borderColor: THEMES.royal.border }}
                  >
                    <span
                      className="font-display italic text-lg mt-0.5"
                      style={{ color: THEMES.royal.accent, fontVariationSettings: '"SOFT" 100' }}
                    >
                      {f.n}
                    </span>
                    <div>
                      <div
                        className="font-display text-lg md:text-xl leading-snug"
                        style={{ fontVariationSettings: '"SOFT" 40', color: THEMES.royal.text }}
                      >
                        {f.title}
                      </div>
                      <div className="mt-1 text-sm font-light leading-relaxed" style={{ color: THEMES.royal.textMuted }}>
                        {f.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <ExampleGallery
              testid="foundation-example"
              title="Sample study plans handed to enrolled students."
              subtitle="Every aspirant receives detailed, week-by-week curriculum documents like these — theme-based, source-linked, and calibrated to their target year."
              images={PROGRAM_EXAMPLES.foundation}
              theme={THEMES.royal}
            />
          </ThemedCard>
        </div>

        {/* ============ PROGRAM 02 · PRELIMS CRASH (EMERALD & GOLD) ============ */}
        <div className="mt-14 md:mt-20">
          <ThemedCard id="program-prelims" testid="program-prelims" theme={THEMES.emerald}>
            <ProgramHeader
              number="02"
              tag="Prelims Blitz · Feb 2027"
              title={
                <>
                  UPSC 2027{" "}
                  <span className="italic font-light" style={{ color: THEMES.emerald.accent }}>
                    Prelims Crash Course
                  </span>
                  .
                </>
              }
              tagline="High-intensity, PYQ-driven revision. This phase decides whether you write Mains, or wait another year."
              theme={THEMES.emerald}
              Icon={Zap}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              <InfoCard Icon={Calendar} label="Duration" value="Feb – April 2027" sub="Every Fri & Mon · 7–9 PM" theme={THEMES.emerald} />
              <InfoCard Icon={BookOpen} label="Sessions" value="22 Total" sub="Online mode only" theme={THEMES.emerald} />
              <InfoCard Icon={Award} label="Free Orientation" value="February 2027" sub="Notified after registration" theme={THEMES.emerald} />
            </div>

            {/* Highlights + subjects */}
            <div className="mt-10 md:mt-14 grid grid-cols-12 gap-6 md:gap-10">
              <div className="col-span-12 md:col-span-7">
                <div
                  className="text-[10px] tracking-[0.32em] uppercase font-semibold mb-5"
                  style={{ color: THEMES.emerald.accent }}
                >
                  Highlights
                </div>
                <ul className="space-y-3">
                  {[
                    "Prelims-centric & elimination-based learning",
                    "PYQ-driven coverage (last 15 years)",
                    "High-yield topics in limited time",
                    "Smart revision techniques for maximum score",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-base md:text-lg font-light" style={{ color: THEMES.emerald.text }}>
                      <Check size={18} strokeWidth={2} style={{ color: THEMES.emerald.accent }} className="mt-1 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-span-12 md:col-span-5">
                <div
                  className="text-[10px] tracking-[0.32em] uppercase font-semibold mb-5"
                  style={{ color: THEMES.emerald.accent }}
                >
                  Subjects Covered
                </div>
                <div className="flex flex-wrap gap-2">
                  {["Polity", "Economy", "History", "Geography", "Environment"].map((s) => (
                    <span
                      key={s}
                      className="px-4 py-2 rounded-full text-sm font-light"
                      style={{
                        background: THEMES.emerald.pill,
                        border: `1px solid ${THEMES.emerald.border}`,
                        color: THEMES.emerald.text,
                      }}
                    >
                      {s}
                    </span>
                  ))}
                </div>

                {/* Fees */}
                <div
                  className="mt-8 pt-6 border-t"
                  style={{ borderColor: THEMES.emerald.border }}
                >
                  <div
                    className="text-[10px] tracking-[0.32em] uppercase font-semibold mb-3"
                    style={{ color: THEMES.emerald.textMuted }}
                  >
                    Fees
                  </div>
                  <div className="flex items-baseline gap-4 flex-wrap">
                    <span
                      className="font-display text-4xl md:text-5xl leading-none"
                      style={{ fontVariationSettings: '"SOFT" 30', color: THEMES.emerald.accent }}
                    >
                      ₹5,499
                    </span>
                    <span
                      className="font-display italic text-lg md:text-xl line-through"
                      style={{ color: THEMES.emerald.strike }}
                    >
                      ₹7,999
                    </span>
                    <span
                      className="text-[10px] tracking-[0.28em] uppercase px-3 py-1 font-semibold rounded-full"
                      style={{
                        color: THEMES.emerald.accent,
                        border: `1px solid ${THEMES.emerald.borderStrong}`,
                      }}
                    >
                      Save ₹2,500
                    </span>
                  </div>
                  <p className="mt-3 text-sm font-light" style={{ color: THEMES.emerald.textMuted }}>
                    <span className="font-medium" style={{ color: THEMES.emerald.accent }}>
                      Old students
                    </span>{" "}
                    — extra 10% discount. Limited-period launch offer.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-10 md:mt-12 flex flex-wrap items-center gap-4">
              <a
                href={PRELIMS_CRASH_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="prelims-apply-btn"
                className="px-8 md:px-10 py-4 md:py-5 text-sm rounded-full font-semibold inline-flex items-center gap-3 hover:scale-[1.02] transition-transform duration-300"
                style={{ background: THEMES.emerald.ctaBg, color: THEMES.emerald.ctaText }}
              >
                Register for Crash Course — ₹5,499 <span aria-hidden>→</span>
              </a>
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="prelims-whatsapp-btn"
                className="inline-flex items-center px-7 md:px-8 py-4 md:py-5 text-sm rounded-full font-medium transition-colors duration-300"
                style={{
                  border: `1px solid ${THEMES.emerald.borderStrong}`,
                  color: THEMES.emerald.text,
                }}
              >
                Ask on WhatsApp
              </a>
            </div>

            <ExampleGallery
              testid="prelims-example"
              title="Inside the crash-course curriculum."
              subtitle="A quick look at the 22-session Prelims blitz — PYQ-mapped, subject-wise, revision-first."
              images={PROGRAM_EXAMPLES.prelims}
              theme={THEMES.emerald}
            />
          </ThemedCard>
        </div>

        {/* ============ PROGRAM 03 · ANSWER WRITING (BURGUNDY & SILVER) ============ */}
        <div className="mt-14 md:mt-20">
          <ThemedCard id="program-answer-writing" testid="program-answer-writing" theme={THEMES.burgundy}>
            <ProgramHeader
              number="03"
              tag="Exclusive · By Enrolment Only"
              title={
                <>
                  365-Day{" "}
                  <span className="italic font-light" style={{ color: THEMES.burgundy.accent }}>
                    Answer Writing
                  </span>{" "}
                  Evaluation.
                </>
              }
              tagline="Quality evaluation. Consistent improvement. UPSC success."
              theme={THEMES.burgundy}
              Icon={Feather}
            />

            <p
              className="text-lg leading-relaxed font-light max-w-4xl"
              style={{ color: THEMES.burgundy.textMuted }}
            >
              Master UPSC Mains answer writing through systematic evaluation of Previous Year Questions (PYQs). Every answer is reviewed personally with detailed, actionable feedback — structure, content, presentation, value addition, exam-oriented improvements. Track your progress throughout the year and build the consistency required to excel in Mains — all at an affordable fee with personalised mentorship.
            </p>

            {/* Bullets — 3-col grid */}
            <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
              {[
                "Personalised, detailed copy evaluation",
                "Examiner-style feedback",
                "Actionable improvement points for every answer",
                "Model framework / ideal answer structure",
                "Content enrichment suggestions",
                "Introduction & conclusion improvement",
                "Presentation, flow & structure feedback",
                "Value addition (facts, examples, committees, case studies)",
                "Individual progress tracking",
                "Common mistakes analysis",
                "Answer writing strategy",
                "Mains-oriented thinking development",
                "Flexible submission schedule",
                "Minimal fees, maximum value",
                "Limited seats for personal attention",
              ].map((b) => (
                <div key={b} className="flex items-start gap-3 py-2">
                  <Check size={16} strokeWidth={2} style={{ color: THEMES.burgundy.accent }} className="mt-1.5 shrink-0" />
                  <span className="text-base font-light leading-relaxed" style={{ color: THEMES.burgundy.text }}>
                    {b}
                  </span>
                </div>
              ))}
            </div>

            {/* Feature tags */}
            <div className="mt-10 md:mt-12 flex flex-wrap gap-3">
              {["Majorly PYQs", "Personal Attention", "Limited Seats", "Flexible Submission", "Affordable Fees"].map((t) => (
                <span
                  key={t}
                  className="px-4 py-2 rounded-full text-sm font-light"
                  style={{
                    background: THEMES.burgundy.pill,
                    border: `1px solid ${THEMES.burgundy.border}`,
                    color: THEMES.burgundy.text,
                  }}
                >
                  {t}
                </span>
              ))}
            </div>

            <ExampleGallery
              testid="answer-writing-example"
              title="See what evaluated feedback actually looks like."
              subtitle="Real Mains copies with Sir's annotations — sidehead marking, structural corrections, value-addition suggestions, and framework rewrites. This is exactly what every enrolled aspirant receives."
              images={PROGRAM_EXAMPLES.answerWriting}
              theme={THEMES.burgundy}
            />

            {/* CTA panel */}
            <div
              className="mt-12 md:mt-14 p-6 md:p-8 rounded-2xl grid grid-cols-12 gap-6 items-center"
              style={{
                background: THEMES.burgundy.cardBg,
                border: `1px solid ${THEMES.burgundy.borderStrong}`,
              }}
            >
              <div className="col-span-12 md:col-span-8">
                <div
                  className="text-[10px] tracking-[0.32em] uppercase font-semibold mb-3"
                  style={{ color: THEMES.burgundy.accent }}
                >
                  For enrolment
                </div>
                <h4
                  className="font-display text-2xl md:text-3xl lg:text-4xl leading-tight"
                  style={{ fontVariationSettings: '"SOFT" 40', color: THEMES.burgundy.text }}
                >
                  Contact NavSankalpIAS{" "}
                  <span className="italic" style={{ color: THEMES.burgundy.accent }}>
                    directly.
                  </span>
                </h4>
                <p
                  className="mt-3 text-base font-light leading-relaxed max-w-2xl"
                  style={{ color: THEMES.burgundy.textMuted }}
                >
                  Seats are strictly limited to ensure personal attention on every evaluated copy.
                </p>
              </div>
              <div className="col-span-12 md:col-span-4 flex flex-col gap-3 md:items-end">
                <a
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="answer-writing-whatsapp-btn"
                  className="px-7 py-4 text-xs tracking-[0.24em] uppercase font-semibold rounded-full text-center min-w-[240px] hover:scale-[1.02] transition-transform duration-300"
                  style={{ background: THEMES.burgundy.ctaBg, color: THEMES.burgundy.ctaText }}
                >
                  WhatsApp to Enrol
                </a>
                <a
                  href={`tel:${CONTACT.phoneIntl}`}
                  data-testid="answer-writing-call-btn"
                  className="inline-flex items-center justify-center px-7 py-4 text-xs tracking-[0.24em] uppercase font-semibold rounded-full min-w-[240px] transition-colors duration-300"
                  style={{
                    border: `1px solid ${THEMES.burgundy.borderStrong}`,
                    color: THEMES.burgundy.text,
                  }}
                >
                  Call +91 {CONTACT.phone}
                </a>
              </div>
            </div>
          </ThemedCard>
        </div>
      </div>
    </section>
  );
}
